import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from 'library/hooks/use-debounce';
import { CSSTransition } from 'react-transition-group';
import { ActionIconContainer } from 'library/styles/icon';
import {
  getSuggestedItems as getSuggestedItemsAction,
} from 'store/suggested-items/actions';
import { showContentOverlay, hideContentOverlay } from 'store/shared/actions';
import { getContentOverlayDisplayState } from 'store/shared/selectors';
import * as yup from 'yup';
import {
  Container, Icon, SearchInput, StyledClickOutsideContainer,
} from './styles';
import Options from './options';
import SearchByColorModal from '../modal/pick-color';

const DELAY = 600;

const Search = ({
  types = [],
  onSelect,
  queryParameter = null,
  onReset,
  loading,
  options = [],
  placeholder = '',
  hideOverlay = false,
  showSearchByColor = true,
  isFocusedOnMount = false,
}) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const fieldRef = useRef();
  const [isOpened, setIsOpened] = useState(false);
  const [isSearchByColorModalOpened, toggleSearchByColorModal] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, DELAY);
  const isOverlayDisplayed = useSelector(getContentOverlayDisplayState);

  useEffect(
    () => {
      if (debouncedSearchTerm) {
        dispatch(getSuggestedItemsAction({ searchTerm: debouncedSearchTerm, types }));

        if (!isOpened) {
          setIsOpened(true);
        }
      }
    },
    [debouncedSearchTerm],
  );

  useEffect(
    () => {
      if (isOpened) {
        if(!hideOverlay) {
          dispatch(showContentOverlay());
        }
      } else {
        if(onReset) {
          onReset();
        }

        if(isOverlayDisplayed) {
          dispatch(hideContentOverlay());
        }
      }
    },
    [isOpened],
  );

  useEffect(
    () => {
      if (!searchTerm) {
        setIsOpened(false);
      }
    },
    [searchTerm],
  );

  useEffect(
    () => {
      if(isFocusedOnMount) {
        fieldRef.current?.focus();
      }
    },
    [isFocusedOnMount],
  );

  const handleSelect = (selectedId) => {
    const selectedOption = options.find(({ id }) => id === selectedId);
    // eslint-disable-next-line no-console
    setIsOpened(false);
    setSearchTerm('');
    onSelect(selectedOption);
    onReset();
  };

  const handleClickOutside = () => {
    setIsOpened(false);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value ?? '');

    if (e.target.value) {
      setIsOpened(true);
    } else {
      setIsOpened(false);
    }
  };

  const handleSearchByColorModalOpen = () => {
    toggleSearchByColorModal(true);
  };

  const handleSearchByColorModalClose = () => {
    toggleSearchByColorModal(false);
  };

  const handleEnterPush = (event) => {
    if(event.key === 'Enter') {
      setIsOpened(false);
      setSearchTerm('');
      onSelect({ id: queryParameter, type: queryParameter, name: searchTerm });

      if(onReset) {
        onReset();
      }
    }
  };

  const onSearchByColor = (values) => {
    onSelect({
      type: 'color',
      values: values.color.map((id) => ({ id, name: id, type: 'color' })),
    });

    toggleSearchByColorModal(false);
  };

  const validation = yup.object().shape({
    color: yup.array().of(yup.string().required('Color is required')).min(1),
  });

  return (
    types.length === 0 ? null : (
      <Container isOpened={isOpened}>
        <StyledClickOutsideContainer onClickOutside={handleClickOutside}>
          <SearchInput>
            <ActionIconContainer>
              <Icon className="icon-magnifier icon-search" />
            </ActionIconContainer>
            <input
              type="text"
              ref={fieldRef}
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleSearch}
              disabled={types.length === 0}
              onKeyPress={queryParameter ? handleEnterPush : undefined}
            />
            <ActionIconContainer>
              {
                showSearchByColor ? (
                  <Icon
                    onClick={handleSearchByColorModalOpen}
                    className="icon-colors"
                  />
                ) : null
              }
              <SearchByColorModal
                text=""
                isCancelDisplayed={false}
                contrastingFooter
                name="color"
                title="Color picker"
                submitText="Search for selected colors"
                isOpened={isSearchByColorModalOpened}
                onClose={handleSearchByColorModalClose}
                onSubmit={onSearchByColor}
                validation={validation}
                defaultValues={{ color: [] }}
                overlayClassName="Overlay"
              />
            </ActionIconContainer>
          </SearchInput>
          {
            options.length ? (
              <CSSTransition
                mountOnEnter
                unmountOnExit
                in={!!isOpened}
                appear={!!isOpened}
                timeout={300}
                classNames="fade"
              >
                <div>
                  <Options
                    loading={loading}
                    options={options}
                    onSelect={handleSelect}
                  />
                </div>
              </CSSTransition>
            ) : null
          }

        </StyledClickOutsideContainer>
      </Container>
    )
  );
};

export default Search;
